import React, { useState, useEffect, useRef, useCallback } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg";
import "../vendor/vendorList.css";
import { apiCallGeneric } from "../../services/api-service.js";
import { X } from "lucide-react";
import ReactDOM from "react-dom";

const MultiSelectDropdown = ({
  apiUrl,
  placeholder,
  value = [], // <-- now array
  onSelect, // returns full selected list
  handleClear,
}) => {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceTimer = useRef(null);

  const fetchData = async (term = "") => {
    if (!apiUrl) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await apiCallGeneric("get", apiUrl, null, {
        params: { term },
      });
      if (response.status === 200 && response.data.success) {
        setOptions(response.data.suggestionlist || []);
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load when dropdown opens
  useEffect(() => {
    if (open) fetchData(search);
  }, [open]);

  // Debounced re-fetch on search input (only while open)
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchData(search), 300);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  const toggleSelection = (option) => {
    const exists = value.some((v) => v.value === option.value);
    const updated = exists
      ? value.filter((v) => v.value !== option.value)
      : [...value, option];
    onSelect(updated);
  };

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    placement: "bottom",
  });
  const containerRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const menuMaxHeight = 250; // approx max height of the menu

      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < menuMaxHeight && spaceAbove > spaceBelow) {
        setMenuPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          placement: "top",
        });
      } else {
        setMenuPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          placement: "bottom",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [open, updatePosition]);

  const menu = (
    <Dropdown.Menu
      renderOnMount
      style={{
        position: "fixed",
        top:
          menuPosition.placement === "bottom"
            ? `${menuPosition.top + 5}px`
            : "auto",
        bottom:
          menuPosition.placement === "top"
            ? `${window.innerHeight - menuPosition.top + 5}px`
            : "auto",
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #EBEBEB",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        minWidth: "200px",
        overflowY: "auto",
        maxHeight: "220px",
        zIndex: 10001,
        margin: 0,
      }}
    >
      <div style={{ padding: "8px" }}>
        <input
          type="text"
          className="fs-14 fw-500 textcolor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${placeholder}`}
          style={{
            width: "100%",
            padding: "6px 8px",
            borderRadius: "8px",
            border: "1px solid #EBEBEB",
          }}
        />
      </div>

      {loading ? (
        <Dropdown.Item disabled>Loading...</Dropdown.Item>
      ) : options.length === 0 ? (
        <Dropdown.Item disabled>No results found</Dropdown.Item>
      ) : (
        options.map((option) => {
          const checked = value.some((v) => v.value === option.value);
          return (
            <Dropdown.Item
              className="fs-14 fw-500 textcolor d-flex align-items-center"
              key={option.value}
              onClick={() => toggleSelection(option)}
            >
              <input
                className="custom-checkbox"
                type="checkbox"
                checked={checked}
                readOnly
                style={{ marginRight: "8px" }}
              />
              {option.label}
            </Dropdown.Item>
          );
        })
      )}
    </Dropdown.Menu>
  );

  const [isFocused, setIsFocused] = useState(false);
  const isActive = open || isFocused || (value && value.length > 0);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Dropdown show={open} onToggle={setOpen}>
        <Dropdown.Toggle
          variant="light"
          id={`dropdown-${placeholder ? placeholder.toLowerCase().replace(/\s+/g, "-") : "multi"}`}
          className={`d-flex align-items-center justify-content-between filter-dropdown-btn ${
            isActive ? "active" : ""
          }`}
          style={{
            borderRadius: "12px",
            border: isActive ? "1px solid #4F46E5" : "1px solid #EBEBEB",
            boxShadow: isActive ? "0 0 0 3px rgba(79, 70, 229, 0.18)" : "none",
            padding: "6px 12px",
            backgroundColor: isActive ? "#F5F3FF" : "white",
            outline: "none",
            width: "100%",
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <span className="fs-12 fw-400 textGrey">{placeholder} :</span>
          <span className="d-flex align-items-center px-1">
            <span
              className="dropdown-text"
              title={
                value.length ? value.map((v) => v.label).join(", ") : "Select"
              }
            >
              {value.length ? value.map((v) => v.label).join(", ") : "Select"}
            </span>

            <span className="dropdown-toggle-arrow textPurple">
              <DownArrow />
            </span>

            {value.length > 0 && (
              <X
                size={18}
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                  setIsFocused(false);
                }}
              />
            )}
          </span>
        </Dropdown.Toggle>
        {ReactDOM.createPortal(menu, document.body)}
      </Dropdown>
    </div>
  );
};

export default MultiSelectDropdown;
